<script setup lang="ts">
import { ref } from "vue";
import { UiText, UiIcon, UiChip, UiTooltip, UiButton, UiAvatar } from "@shopexperts/shared";
import type { AdminWalletTransaction } from "@shopexperts/legacy-api";
import {
  getDirectionLabel,
  getToneAmountClasses,
} from "@/components/expert/wallet/walletTransactionDisplay";
import WalletTransactionRail from "@/components/expert/wallet/WalletTransactionRail.vue";
import WalletTransactionBreakdownList from "@/components/expert/wallet/WalletTransactionBreakdownList.vue";
import WalletTransactionEffect from "@/components/expert/wallet/WalletTransactionEffect.vue";
import { useAdminWalletTransaction } from "./useAdminWalletTransaction";
import AdminTransactionSummaryHeader from "./AdminTransactionSummaryHeader.vue";
import AdminTransactionParties from "./AdminTransactionParties.vue";
import AdminTransactionInternalNote from "./AdminTransactionInternalNote.vue";
import AdminTransactionLevelCommission from "./AdminTransactionLevelCommission.vue";
import AdminTransactionAllocations from "./AdminTransactionAllocations.vue";
import AdminTransactionTrace from "./AdminTransactionTrace.vue";

const props = withDefaults(
  defineProps<{
    transaction: AdminWalletTransaction;
    showOrganization?: boolean;
    isLast?: boolean;
    canDeleteTransaction?: boolean;
  }>(),
  {
    showOrganization: false,
    isLast: false,
    canDeleteTransaction: false,
  },
);

const emit = defineEmits<{
  (e: "release-hold", transaction: AdminWalletTransaction): void;
  (e: "delete-transaction", transaction: AdminWalletTransaction): void;
}>();

const expanded = ref(false);

function toggleExpanded() {
  expanded.value = !expanded.value;
}

function requestReleaseHold() {
  emit("release-hold", props.transaction);
}

function requestDeleteTransaction() {
  emit("delete-transaction", props.transaction);
}

const {
  transaction,
  movement,
  tone,
  timelineState,
  headlineAmount,
  titleLabel,
  fullDate,
  dateTimeLabel,
  releasedByLabel,
  providerFeeLines,
  breakdownRows,
  hasDetails,
  effectTiles,
  references,
  hasLevelCommission,
  appliedLevelTitle,
  levelCommissionRateLabel,
  commissionAmountLabel,
  internalNotesAdmin,
  internalNotesAdminLabel,
  parties,
  clientUser,
  expertUser,
  expertRoute,
  kindChipLabel,
  isDeleted,
  deletedAtLabel,
  deletedByAdmin,
  deletedByAdminLabel,
  deletedSummaryLabel,
  canReleaseHold,
  canRequestDelete,
  allocationRows,
  unallocatedAvailableLabel,
  hasAllocationDetails,
  fundedRows,
  hasFundedDetails,
  adminDetails,
  copyableIdentifiers,
  metadataEntries,
  summaryStats,
} = useAdminWalletTransaction(() => props.transaction, {
  showOrganization: () => props.showOrganization,
});
</script>

<template>
  <li class="font-archivo relative flex gap-0 sm:gap-4">
    <WalletTransactionRail :state="timelineState" :is-last="isLast" />

    <article
      class="mb-3 min-w-0 flex-1 overflow-hidden rounded-xl border bg-se-white transition-all duration-200 hover:shadow-[0_10px_28px_-22px_rgba(16,24,40,0.45)]"
      :class="isDeleted ? 'border-red-300 ring-2 ring-red-100 hover:border-red-400' : 'border-se-gray-200 hover:border-se-gray-300'"
    >
      <div
        class="group/row cursor-pointer transition-colors"
        :class="isDeleted ? 'hover:bg-red-50' : 'hover:bg-se-gray-50'"
        role="button"
        tabindex="0"
        :aria-expanded="expanded"
        :aria-label="`${titleLabel} — ${expanded ? 'collapse' : 'expand'} details`"
        @click="toggleExpanded"
        @keydown.enter.prevent="toggleExpanded"
        @keydown.space.prevent="toggleExpanded"
      >
        <AdminTransactionSummaryHeader
          :transaction-id="transaction.id"
          :kind-label="kindChipLabel"
          :full-date="fullDate"
          :date-time-label="dateTimeLabel"
          :expert-user="expertUser"
          :client-user="clientUser"
          :expert-route="expertRoute"
          :stats="summaryStats"
          :expanded="expanded"
          :is-deleted="isDeleted"
          :deleted-summary-label="deletedSummaryLabel"
        />
      </div>

      <div
        class="grid transition-[grid-template-rows] duration-300 ease-out"
        :class="expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'"
      >
        <div class="min-h-0 overflow-hidden">
          <div class="border-t border-se-gray-200">
            <div class="flex flex-col gap-3 p-4">
              <div class="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-4">
                <div class="min-w-0">
                  <div class="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
                    <UiTooltip content="Transaction ID">
                      <UiText
                        as="span"
                        variant="p-small"
                        :text="`#${transaction.id}`"
                        class="shrink-0 font-semibold text-se-gray-500"
                      />
                    </UiTooltip>
                    <UiText as="p" variant="p" :text="titleLabel" class="min-w-0 font-semibold text-se-dark" />
                  </div>

                  <div class="mt-1 flex flex-wrap items-center gap-1.5">
                    <UiChip :text="kindChipLabel" variant="neutral" size="xs" rounded="full" />
                    <UiChip :text="getDirectionLabel(transaction.direction)" variant="plain" size="xs" rounded="full" />
                    <UiTooltip :content="fullDate">
                      <UiText as="span" variant="p-small" :text="dateTimeLabel" class="text-se-gray-700" />
                    </UiTooltip>
                    <UiText
                      v-if="releasedByLabel"
                      as="span"
                      variant="p-small"
                      :text="releasedByLabel"
                      class="ml-1.5 font-medium text-se-green before:mr-1.5 before:text-se-gray-500 before:content-['•']"
                    />
                  </div>
                </div>

                <div class="flex shrink-0 flex-col items-end gap-1 text-right">
                  <div class="flex items-center justify-end gap-1.5" :class="getToneAmountClasses(tone)">
                    <UiIcon v-if="headlineAmount.icon" :name="headlineAmount.icon" class="h-5 w-5" />
                    <UiText as="span" variant="h4" :text="headlineAmount.label" class="font-semibold" />
                  </div>
                  <div class="flex items-center gap-0.5">
                    <UiText as="span" variant="caption-normal" :text="movement.label" />
                    <UiTooltip :content="movement.description">
                      <UiIcon name="info-circle" class="h-2.5 w-2.5 text-se-gray-700" />
                    </UiTooltip>
                  </div>
                  <UiButton
                    v-if="canReleaseHold"
                    variant="outline"
                    size="sm"
                    label="Release hold"
                    icon="lock"
                    @click.stop="requestReleaseHold"
                  />
                  <UiButton
                    v-if="canDeleteTransaction && canRequestDelete"
                    variant="outline"
                    size="sm"
                    label="Delete"
                    icon="delete"
                    @click.stop="requestDeleteTransaction"
                  />
                </div>
              </div>

              <div v-if="isDeleted" class="rounded-lg border border-red-300 bg-red-50 px-4 py-3">
                <div class="flex flex-wrap items-center gap-2">
                  <UiIcon name="delete" class="h-4 w-4 text-red-700" />
                  <UiText as="span" variant="p-small" text="Deleted transaction" class="font-semibold text-red-900" />
                  <UiText
                    v-if="deletedAtLabel"
                    as="span"
                    variant="caption-normal"
                    :text="deletedAtLabel"
                    class="text-red-700"
                  />
                </div>
                <div v-if="deletedByAdminLabel" class="mt-1 flex items-center gap-1.5 text-red-800">
                  <UiText as="span" variant="caption-normal" class="text-red-800" text="Deleted by" />
                  <span class="inline-flex min-w-0 items-center gap-1">
                    <UiAvatar
                      v-if="deletedByAdmin"
                      :name="deletedByAdmin.name ?? deletedByAdmin.email ?? undefined"
                      :url="deletedByAdmin.avatar ?? undefined"
                      size="inline"
                      :responsive="false"
                      color-coded
                    />
                    <UiText
                      as="span"
                      variant="caption-normal"
                      :text="deletedByAdminLabel"
                      class="min-w-0 truncate font-semibold text-red-800"
                    />
                  </span>
                </div>
                <div v-if="transaction.delete_note" class="mt-3 rounded-md border border-red-200 bg-se-white px-3 py-2">
                  <UiText as="span" variant="caption" text="Admin deletion note" class="text-red-700" />
                  <UiText as="p" variant="small" :text="transaction.delete_note" class="text-red-950" />
                </div>
              </div>

              <AdminTransactionParties :parties="parties" />

              <div v-if="transaction.public_notes" class="rounded-md bg-se-gray-100 px-3 py-2">
                <UiText as="span" variant="caption" class="text-se-gray-500" text="Public note" />
                <UiText as="p" variant="small" :text="transaction.public_notes" class="text-se-dark" />
              </div>

              <AdminTransactionInternalNote
                v-if="transaction.internal_notes"
                :note="transaction.internal_notes"
                :admin="internalNotesAdmin"
                :admin-label="internalNotesAdminLabel"
              />

              <AdminTransactionLevelCommission
                v-if="hasLevelCommission"
                :level-title="appliedLevelTitle"
                :commission-label="commissionAmountLabel"
                :rate-label="levelCommissionRateLabel"
              />
            </div>

            <section v-if="hasDetails" class="border-t border-se-gray-200 px-4 py-4">
              <UiText as="h4" variant="p-small" text="Breakdown" class="font-semibold text-se-dark" />
              <WalletTransactionBreakdownList
                class="mt-3"
                :rows="breakdownRows"
                :provider-fee-lines="providerFeeLines"
                :currency="transaction.currency"
              />
            </section>

            <WalletTransactionEffect heading="Effect on wallet" :tiles="effectTiles" :references="references" />

            <AdminTransactionAllocations
              v-if="hasAllocationDetails"
              :rows="allocationRows"
              :unallocated-label="unallocatedAvailableLabel"
            />

            <AdminTransactionAllocations
              v-if="hasFundedDetails"
              :rows="fundedRows"
              heading="Used to fund"
              heading-tooltip="The debits — payouts, refunds — covered by the available funds this transaction added, matched oldest first."
            />

            <AdminTransactionTrace
              :details="adminDetails"
              :copyable-identifiers="copyableIdentifiers"
              :metadata="metadataEntries"
            />
          </div>
        </div>
      </div>
    </article>
  </li>
</template>
